import type { Slot, PollInsights } from "./types";

export interface DayScore {
  /** Date ISO YYYY-MM-DD utilisée comme clé stable + dans l'URL `?view=day:…` */
  date: string;
  /** Slot IDs appartenant à ce jour, dans l'ordre des slots */
  slotIds: string[];
  /** Meilleur `yes` parmi les slots du jour (sert au tri / au highlight) */
  bestYes: number;
  /** Total yes cumulé sur tous les slots du jour (tie-breaker) */
  totalYes: number;
}

/**
 * Groupement par date primaire. Les slots multi-dates (groupes week-end /
 * semaine) sont rangés sous leur `primaryDate` — cohérent avec la table qui
 * affiche déjà le groupLabel sur la première date.
 */
export function dayScores(slots: Slot[], insights: PollInsights): DayScore[] {
  const byDate = new Map<string, DayScore>();
  const scoreById = new Map<string, { yes: number }>();
  for (const sc of insights.scores) {
    scoreById.set(sc.slotId, { yes: sc.yes });
  }
  for (const s of slots) {
    const key = s.primaryDate;
    let entry = byDate.get(key);
    if (!entry) {
      entry = { date: key, slotIds: [], bestYes: 0, totalYes: 0 };
      byDate.set(key, entry);
    }
    entry.slotIds.push(s.id);
    const yes = scoreById.get(s.id)?.yes ?? 0;
    entry.totalYes += yes;
    if (yes > entry.bestYes) entry.bestYes = yes;
  }
  return [...byDate.values()].sort((a, b) => a.date.localeCompare(b.date));
}

/**
 * Date (YYYY-MM-DD) du jour dont le meilleur slot a le plus de `yes`. En cas
 * d'égalité, tie-break sur `totalYes`, puis ordre chrono.
 */
export function topDayOf(slots: Slot[], insights: PollInsights): string | null {
  const days = dayScores(slots, insights);
  if (days.length === 0) return null;
  const sorted = [...days].sort((a, b) => {
    if (b.bestYes !== a.bestYes) return b.bestYes - a.bestYes;
    if (b.totalYes !== a.totalYes) return b.totalYes - a.totalYes;
    return a.date.localeCompare(b.date);
  });
  return sorted[0].date;
}
